import { memo, useRef, useMemo, useCallback, useState, useEffect, ReactElement } from 'react';
import { ProForm, ProFormDatePicker, ProFormDigit } from '@ant-design/pro-components';
import { DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Alert, Col, Flex, Row } from 'antd';
import dayjs from 'dayjs';

import {
  Crud,
  CrudMode,
  IconButton,
  renderCrudFormSortableGroup,
  notification,
  rejectionError,
  transformCrudFormErrors,
} from '@ohpen/enso-ui';
import type { CrudFormComponent, CrudFormGetDraggableRow, CrudProps, RenderFormItem, ValidationError } from '@ohpen/enso-ui';
import {
  ChangeRequestOfErcProfileCrudRequest,
  ErcProfile,
  ErcProfilesCreateChangeRequestResponse,
  PenaltyTierRequest,
  UsageResponse,
  ercProfilesCheckUsage,
  ercProfilesCreateChangeRequest,
} from '@pmp/api';
import { crudToActionMap, changeRequestNotificationMessage, changeRequestButtonMessage } from '@pmp/utils';
import { useFeatureToggle } from '@pmp/hooks';

import { getFormSchema } from '../formConfig';
import type { ErcProfileCrudFromData, ErcProfilesCrudFormProps } from '../interfaces';
import styles from '../styles.module.css';

type ErcBandValidationErrors = {
  order?: string[];
  durationInMonths: string[];
  penaltyPercentage: string[];
  endDate: string[];
};
type ErcValidationErrors = {
  ercProfileId: string[];
  name: string[];
  penaltyTiers: Record<string, ErcBandValidationErrors>;
};

export const ErcProfilesCrudForm = memo(({ mode, record, refreshTable, onClose }: ErcProfilesCrudFormProps): ReactElement | null => {
  const ref = useRef<CrudFormComponent>(null);
  const [open, setOpen] = useState(false);
  const [isUsed, setIsUsed] = useState<boolean | null>(null);
  const { t } = useTranslation();
  const enableFourEyes = useFeatureToggle(['enableFourEyes']);

  const handleOnClose = useCallback(() => {
    setOpen(false);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    if (isUsed !== null) return;
    if (mode !== CrudMode.EDIT) {
      setIsUsed(false);
      setOpen(true);
      return;
    }
    if (!record?.ercProfileId) {
      notification().error({ message: t('global.errors.unexpected') });
      handleOnClose();
      return;
    }
    ercProfilesCheckUsage({ path: { ercProfileId: record.ercProfileId } })
      .then(({ data: { isUsed } }: { data: UsageResponse }) => {
        setIsUsed(isUsed);
        setOpen(true);
      })
      .catch(() => {
        notification().error({ message: t('global.errors.unexpected') });
        handleOnClose();
      });
  }, [mode, record, isUsed, t, handleOnClose]);

  const onConfirm: (formData: ErcProfileCrudFromData) => Promise<ValidationError[] | void> = useCallback(
    ({ rollingOrFixed, ...values }) => {
      if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError('mode is required'));
      const penaltyTiers = (values.penaltyTiers ?? [{}]).map((band: PenaltyTierRequest, index: number) => ({
        order: index + 1,
        endDate: rollingOrFixed && band.endDate ? dayjs(band.endDate).format('YYYY-MM-DD') : undefined,
        durationInMonths: rollingOrFixed ? undefined : band.durationInMonths,
        penaltyPercentage: band.penaltyPercentage,
      }));
      const body: ChangeRequestOfErcProfileCrudRequest = {
        data: { ...values, penaltyTiers },
        actionType: crudToActionMap[mode],
      };
      return ercProfilesCreateChangeRequest({ body }).then(
        ({ data: { isValid, errors } }: { data: ErcProfilesCreateChangeRequestResponse }) => {
          if (!isValid) {
            const form = ref.current?.form;
            if (form) {
              const errorFields = form.getFieldsError().map(f => ({ name: f.name, errors: [] }));
              if (errorFields.length) form.setFields(errorFields);
            }
            const penaltyTiers = errors?.penaltyTiers
              ? Object.fromEntries(
                  Object.entries(errors.penaltyTiers as ErcValidationErrors['penaltyTiers']).map(([index, penaltyTierErrors]) => {
                    return [
                      index,
                      {
                        ...penaltyTierErrors,
                        durationInMonths: rollingOrFixed ? [] : penaltyTierErrors.durationInMonths,
                        endDate: rollingOrFixed ? penaltyTierErrors.endDate : [],
                      },
                    ];
                  }),
                )
              : {};
            const allErrors = transformCrudFormErrors({ ...errors, penaltyTiers });
            return allErrors;
          }
          if (mode === CrudMode.EDIT) refreshTable();
          notification().success({
            message: changeRequestNotificationMessage(t, enableFourEyes),
          });
        },
      );
    },
    [mode, refreshTable, t, enableFourEyes],
  );

  const getRow: CrudFormGetDraggableRow = useCallback(
    (index, onDeleteRow) => {
      const disabled = mode === CrudMode.VIEW || !!isUsed;
      const fixed = ref.current?.form.getFieldValue(['rollingOrFixed']);

      return (
        <ProForm.Group>
          <ProFormDatePicker
            name={['penaltyTiers', index, 'endDate']}
            placeholder=""
            fieldProps={{ disabled }}
            hidden={!fixed}
            colProps={{ span: 11 }}
            dependencies={['type']}
          />
          <ProFormDigit
            name={['penaltyTiers', index, 'durationInMonths']}
            placeholder=""
            fieldProps={{
              disabled,
              precision: 0,
            }}
            hidden={fixed}
            colProps={{ span: 11 }}
            dependencies={['type']}
          />
          <ProFormDigit
            name={['penaltyTiers', index, 'penaltyPercentage']}
            placeholder=""
            fieldProps={{ disabled, precision: 2, addonAfter: '%' }}
            colProps={{ span: 11 }}
          />
          <ProForm.Item>
            <IconButton
              type="text"
              icon={<DeleteOutlined rev={undefined} />}
              onClick={() => onDeleteRow?.(index)}
              disabled={disabled || !onDeleteRow}
            />
          </ProForm.Item>
        </ProForm.Group>
      );
    },
    [isUsed, mode],
  );

  const renderAlert: RenderFormItem<ErcProfile> = useCallback(
    (_schema, _config, _form) => {
      if (mode === CrudMode.EDIT && isUsed) return <Alert message={t('pmp.erc.labels.ercInUseMessage')} type="warning" showIcon />;
      return null;
    },
    [mode, isUsed, t],
  );

  const renderErcBands: RenderFormItem<ErcProfile> = useCallback(
    (schema, config, form) => {
      const addButton = { label: t('pmp.fees.labels.addBand') };
      const disabled = mode === CrudMode.VIEW || !!isUsed;
      const minLength = 1;
      const fixed = ref.current?.form.getFieldValue(['rollingOrFixed']);

      return (
        <>
          <Flex className={styles.ercBandsTitles}>
            <Row gutter={8}>
              {fixed ? <Col span={11}>{t('pmp.erc.labels.endDate')}</Col> : <Col span={11}>{t('pmp.erc.labels.durationInMonths')}</Col>}
              <Col span={11}>{t('pmp.erc.labels.ercPercentage')}</Col>
              <Col span={1}></Col>
            </Row>
          </Flex>
          {renderCrudFormSortableGroup({
            schema,
            config,
            form,
            addButton,
            getRow,
            disabled,
            minLength,
            setDisableSave: ref.current?.setDisableSave,
          })}
        </>
      );
    },
    [t, mode, isUsed, getRow],
  );

  const title = useMemo(() => {
    if (!mode) return '';
    const titles: { [key: string]: string } = {
      [CrudMode.VIEW]: t('global.labels.viewX', { x: t('pmp.erc.labels.ercProfile') }),
      [CrudMode.CREATE]: t('global.labels.createX', { x: t('pmp.erc.labels.ercProfile') }),
      [CrudMode.EDIT]: t('global.labels.editX', { x: t('pmp.erc.labels.ercProfile') }),
    };
    return titles[mode];
  }, [mode, t]);

  const crudProps: CrudProps<ErcProfileCrudFromData> = useMemo(() => {
    const rollingOrFixed = !!record?.penaltyTiers?.[0]?.endDate;

    return {
      ref,
      open,
      schema: getFormSchema(renderErcBands, renderAlert, mode, isUsed),
      title,
      labels: {
        confirm: mode === CrudMode.EDIT ? t('global.labels.save') : changeRequestButtonMessage(t, enableFourEyes),
      },
      initialValues: { ...record, rollingOrFixed },
      mode,
      onClose: handleOnClose,
      onConfirm,
    };
  }, [record, open, renderErcBands, renderAlert, mode, isUsed, title, t, handleOnClose, onConfirm, enableFourEyes]);

  return <Crud {...crudProps} />;
});
